import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  Chip,
  CircularProgress,
  alpha,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { createFunder, updateFunder } from "../../../api/services/Billing/funder";
import ConfirmModal from "../../ConfirmModal";
import CustomInput from "../../FormFields/CustomInput";
import CustomTextArea from "../../CustomForm/CustomTextArea";
import CustomSelect from "../../FormFields/CustomSelect/CustomSelect";

const claimFormatOptions = [
  { value: "CSV", label: "CSV (Spreadsheet)" },
  { value: "XML", label: "XML (Portal Upload)" },
  { value: "PDF", label: "Manual PDF" },
];

const billingCycleOptions = [
  { value: "WEEKLY", label: "Weekly" },
  { value: "FORTNIGHTLY", label: "Fortnightly" },
  { value: "FOUR_WEEKLY", label: "4-Weekly" },
  { value: "MONTHLY", label: "Monthly" },
];

const FunderDialog = ({ open, handleClose, editData = null, mode = "COUNCIL" }) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [showConfirm, setShowConfirm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    vendor_code: "",
    claim_format: "CSV",
    finance_email: "",
    billing_address: "",
    billing_cycle: "WEEKLY",
    payment_terms: 28,
    commission_rate: 0,
    category: mode,
  });

  useEffect(() => {
    if (editData) {
      setFormData({
        ...editData,
        category: editData.category || mode,
      });
    } else {
      setFormData({
        name: "",
        vendor_code: "",
        claim_format: "CSV",
        finance_email: "",
        billing_address: "",
        billing_cycle: "WEEKLY",
        payment_terms: 28,
        commission_rate: 0,
        category: mode,
      });
    }
  }, [editData, mode, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const mutation = useMutation(
    (data) => (editData ? updateFunder(editData.id, data) : createFunder(data)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("funders");
        enqueueSnackbar(
          editData ? "Funder updated successfully" : "Funder created successfully",
          { variant: "success" }
        );
        setShowConfirm(false);
        handleClose();
      },
      onError: (error) => {
        enqueueSnackbar(error.response?.data?.detail || "An error occurred", {
          variant: "error",
        });
        setShowConfirm(false);
      },
    }
  );

  const handleOpenConfirm = () => setShowConfirm(true);
  const handleExecuteAction = () => mutation.mutate(formData);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
      >
        {/* HEADER */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "#F8FAFC",
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: alpha("#2469F0", 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BusinessIcon sx={{ color: "#2469F0", fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
                {editData ? `Edit ${mode} Profile` : `Register New ${mode}`}
              </Typography>
              <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.7rem" }}>
                {editData ? "Update funder information" : "Add a new funder to the system"}
              </Typography>
            </Box>
          </Stack>
          <Chip
            label={mode}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.65rem",
              fontWeight: 600,
              bgcolor: alpha("#2469F0", 0.08),
              color: "#2469F0",
            }}
          />
        </Box>

        {/* CONTENT */}
        <DialogContent sx={{ pt: 3, px: 2.5 }}>
          <Stack spacing={2}>
            <CustomInput
              label={mode === "COUNCIL" ? "Council Full Name *" : "Agency Name *"}
              name="name"
              value={formData.name}
              onChange={handleChange}
              size="medium"
              fullWidth
              required
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <CustomInput
                  label="Vendor ID / Code"
                  name="vendor_code"
                  value={formData.vendor_code}
                  onChange={handleChange}
                  size="medium"
                  fullWidth
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <CustomSelect
                  label="Preferred Claim Format"
                  name="claim_format"
                  options={claimFormatOptions}
                  value={formData.claim_format}
                  onChange={handleChange}
                  fullWidth
                  placeholder="Select format"
                />
              </Box>
            </Box>

            <CustomInput
              label="Finance Email Address *"
              name="finance_email"
              type="email"
              value={formData.finance_email}
              onChange={handleChange}
              size="medium"
              fullWidth
              required
            />

            <CustomTextArea
              label="Billing Address"
              name="billing_address"
              value={formData.billing_address}
              onChange={handleChange}
              rows={3}
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <CustomSelect
                  label="Billing Cycle"
                  name="billing_cycle"
                  options={billingCycleOptions}
                  value={formData.billing_cycle}
                  onChange={handleChange}
                  fullWidth
                  placeholder="Select cycle"
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <CustomInput
                  label="Payment Terms (Days)"
                  name="payment_terms"
                  type="number"
                  value={formData.payment_terms}
                  onChange={handleChange}
                  size="medium"
                  fullWidth
                />
              </Box>
            </Box>

            {mode === "BROKERAGE" && (
              <CustomInput
                label="Commission Rate (%)"
                name="commission_rate"
                type="number"
                value={formData.commission_rate}
                onChange={handleChange}
                size="medium"
                fullWidth
              />
            )}
          </Stack>
        </DialogContent>

        {/* ACTIONS */}
        <DialogActions sx={{ borderTop: "1px solid #E2E8F0", p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={mutation.isLoading}
            startIcon={<CancelIcon sx={{ fontSize: 18 }} />}
            size="small"
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "6px",
              borderColor: "#D0D5DD",
              color: "#344054",
              px: 3,
              py: 0.75,
              "&:hover": { bgcolor: "#F9FAFB", borderColor: "#98A2B3" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleOpenConfirm}
            disabled={mutation.isLoading}
            startIcon={
              mutation.isLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SaveIcon sx={{ fontSize: 18 }} />
              )
            }
            size="small"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "6px",
              bgcolor: "#2469F0",
              px: 3,
              py: 0.75,
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 2px 8px rgba(36, 105, 240, 0.3)",
              },
            }}
          >
            {editData ? "Update Profile" : "Create Profile"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        show={showConfirm}
        handleClose={() => setShowConfirm(false)}
        handleConfirm={handleExecuteAction}
        isLoading={mutation.isLoading}
        title={editData ? "Update Profile?" : "Register Funder?"}
        body={`Are you sure you want to ${
          editData ? "update the information for" : "register"
        } ${formData.name || "this funder"}?`}
        confirmText={editData ? "Update Now" : "Register Now"}
      />
    </>
  );
};

export default FunderDialog;